import React from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

interface AvatarCircleProps {
  firstName?: string;
  lastName?: string;
  size?: number;
  style?: any;
  photoUrl?: string;
  useHashedColor?: boolean; // when false, use placeholderColor instead of hashed color
  placeholderColor?: string; // background color for circle/photo when not hashing
  initialsColor?: string; // color for initials text
  initialsStyle?: any; // additional text style overrides for initials
  imageBackgroundColor?: string; // background behind image
  showPlaceholderIconIfNoName?: boolean; // when no name and no photo, show icon (default true)
}

export const AvatarCircle: React.FC<AvatarCircleProps> = ({
  firstName = '',
  lastName = '',
  size = 40,
  style,
  photoUrl,
  useHashedColor = true,
  placeholderColor,
  initialsColor,
  initialsStyle,
  imageBackgroundColor,
  showPlaceholderIconIfNoName = true,
}) => {
  const { components } = useThemeColors();

  const getInitials = () => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const hasInitials = () => {
    return Boolean((firstName && firstName.trim()) || (lastName && lastName.trim()));
  };

  const getBackgroundColor = () => {
    // Generate a consistent color based on the name
    const name = `${firstName}${lastName}`.toLowerCase();
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ];

    if (!useHashedColor) {
      return placeholderColor || components.avatar.background;
    }
    if (!name) return placeholderColor || colors[0];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // If we have a photo, prefer rendering it directly
  if (photoUrl) {
    return (
      <Image
        source={{ uri: photoUrl }}
        style={[
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: imageBackgroundColor || placeholderColor || components.avatar.background,
          },
          style,
        ]}
      />
    );
  }

  if (!hasInitials()) {
    // Optional: show profile placeholder SVG when no name data
    if (showPlaceholderIconIfNoName) {
      const ProfilePlaceholder = RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER;
      return (
        <View
          style={[
            styles.container,
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: getBackgroundColor(),
            },
            style,
          ]}
        >
          <ProfilePlaceholder 
            width={size * 0.5} 
            height={size * 0.5} 
            color={initialsColor || '#FFFFFF'}
          />
        </View>
      );
    }
    // Otherwise, render an empty circle to preserve layout
    return (
      <View
        style={[
          styles.container,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: getBackgroundColor(),
          },
          style,
        ]}
      />
    );
  }

  return (
    <View
      style={[
        styles.container,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: getBackgroundColor(),
        },
        style,
      ]}
    >
      <Text
        scale="micro"
        style={[
          styles.initials,
          { fontSize: size * 0.4, color: initialsColor || '#FFFFFF' },
          initialsStyle,
        ]}
      >
        {getInitials()}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
});
